import { Fragment, useState } from 'react';
import { ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { usePermission } from '@/hooks/usePermission';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { mayHaveReachedNetSuite } from '@shared/posting/outboxStatus';
import { useSystemHealthStore } from '../store/systemHealthStore';
import { DeadLetterDetail } from './DeadLetterDetail';
import type { DeadLetterJob } from '../types/systemHealth.types';

const TYPE_LABEL: Record<string, string> = {
  'posting.mis': 'MIS post',
  'posting.ir': 'IR post',
  'posting.so': 'SO post',
  'posting.cl': 'CL post',
  'mrs.status': 'MRS status',
  'webhook.refresh': 'Cache refresh',
  // Document Capture lanes (86d3w2jrj) — these dead-letter here too, and a raw type string is not
  // something an operator should have to translate.
  'docscan.process': 'Scan decode',
  'docscan.replace': 'Replacement decode',
  'docscan.link': 'NetSuite link',
  'tranid.backfill': 'Tran-ID backfill',
};

function timeAgo(iso: string): string {
  const secs = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/**
 * Dead-letter table (86d3peur0 Phase C) — Failed outbox jobs with a Requeue action. Requeue = reset to
 * Pending (the dispatcher retries; handlers are idempotent, so a reached job reconciles rather than
 * double-posts). Read is gated by `system.view_health`; the action needs `system.manage_outbox`, so
 * view-only users see the table but the button is disabled with a reason.
 */
export function DeadLetterTable() {
  const deadLetters = useSystemHealthStore((s) => s.deadLetters);
  const requeue = useSystemHealthStore((s) => s.requeue);
  const canManage = usePermission(PERMISSIONS.SYSTEM_MANAGE_OUTBOX);
  const [busyId, setBusyId] = useState<number | null>(null);
  /**
   * 86d3w2jrj FE-4 — which job is expanded into its diagnosis. Collapsed by default: the table is a
   * triage list, and the detail is what you open once you've decided which row to think about.
   */
  const [openId, setOpenId] = useState<number | null>(null);

  const handleRequeue = async (job: DeadLetterJob) => {
    setBusyId(job.id);
    try {
      await requeue(job.id);
      toast.success('Requeued — the dispatcher will retry this job.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Requeue failed.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 pb-2">
        <CardTitle className="text-base">Dead letters</CardTitle>
        {deadLetters.length > 0 && (
          <Badge variant="destructive">
            {deadLetters.length} need{deadLetters.length === 1 ? 's' : ''} action
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        <Table containerClassName="max-h-80 overflow-auto">
          <TableHeader>
            <TableRow>
              {/* expander column — header intentionally blank, labelled for assistive tech */}
              <TableHead className="w-8">
                <span className="sr-only">Show details</span>
              </TableHead>
              <TableHead>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={0}
                      className="cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-2 hover:decoration-muted-foreground"
                    >
                      Job
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>The outbox handler + job id.</TooltipContent>
                </Tooltip>
              </TableHead>
              <TableHead>Entity</TableHead>
              <TableHead className="text-right">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={0}
                      className="cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-2 hover:decoration-muted-foreground"
                    >
                      Attempts
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Times the dispatcher tried before dead-lettering.</TooltipContent>
                </Tooltip>
              </TableHead>
              <TableHead>Last error</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {deadLetters.length ? (
              deadLetters.map((job) => {
                const reached = mayHaveReachedNetSuite(job);
                const expanded = openId === job.id;
                return (
                  <Fragment key={job.id}>
                  <TableRow>
                    <TableCell className="w-8 p-0 pl-2 align-middle">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            aria-expanded={expanded}
                            aria-controls={`dead-letter-detail-${job.id}`}
                            aria-label={expanded ? 'Hide job details' : 'Show job details'}
                            title={expanded ? 'Hide job details' : 'Show job details'}
                            onClick={() => setOpenId(expanded ? null : job.id)}
                          >
                            {expanded ? (
                              <ChevronDown className="h-4 w-4" aria-hidden />
                            ) : (
                              <ChevronRight className="h-4 w-4" aria-hidden />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {expanded ? 'Hide job details' : 'Every attempt, defers included, and how far it got'}
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <span className="font-medium">{TYPE_LABEL[job.type] ?? job.type}</span>{' '}
                      <span className="text-xs text-muted-foreground">#{job.id}</span>
                    </TableCell>
                    <TableCell>
                      <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{job.orderingKey ?? '—'}</code>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{job.attempts}</TableCell>
                    <TableCell className="max-w-xs">
                      <div className="flex items-center gap-2">
                        {reached && (
                          <Badge variant="warning" className="shrink-0">
                            may have posted
                          </Badge>
                        )}
                        <span className="truncate text-muted-foreground" title={job.lastError ?? undefined}>
                          {job.lastError ?? '—'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            tabIndex={0}
                            className="cursor-help text-sm text-muted-foreground underline decoration-dotted decoration-muted-foreground/40 underline-offset-2 hover:decoration-muted-foreground"
                          >
                            {timeAgo(job.createdAt)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{new Date(job.createdAt).toLocaleString()}</TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell className="text-right">
                      {canManage ? (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busyId === job.id}
                          onClick={() => handleRequeue(job)}
                        >
                          <RefreshCw className={`mr-2 h-3.5 w-3.5 ${busyId === job.id ? 'animate-spin' : ''}`} />
                          Requeue
                        </Button>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0}>
                              <Button variant="outline" size="sm" disabled>
                                <RefreshCw className="mr-2 h-3.5 w-3.5" />
                                Requeue
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Requires the Manage Work Outbox permission.</TooltipContent>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                  {expanded && (
                    <TableRow>
                      <TableCell id={`dead-letter-detail-${job.id}`} colSpan={7} className="p-0">
                        <DeadLetterDetail job={job} />
                      </TableCell>
                    </TableRow>
                  )}
                  </Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                  No dead-lettered jobs — the outbox is healthy.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
